import React, { useState } from "react";

import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { Button, Chip, Collapse, Popover, Stack } from "@mui/material";
import { useDidUpdate } from "hooks";
import { capitalize } from "lodash";
import { useDealActions, useDealFiltersSelector, useDealQueriesSelect, useReportsActions } from "store";
import { ReportFilters } from "store/types";

import { FilterPicker } from "./FilterPicker";

export const toCapitalizeList: Array<keyof ReportFilters> = ["round", "deal_name"]; //added after capitalize .replace(/_/g," ") in case server changed key of filter Deal Name (replaced with deal_name)

interface FilterPickerState {
  anchorEl: Element;
  options: string[];
  incomingValue: string[];
  onSubmit: (value: string[]) => void;
}

export const Filters: React.FC = () => {
  const { filters: queriesFilters } = useDealQueriesSelect();
  const allFiltersOptions = useDealFiltersSelector();

  const { updateQueries } = useDealActions();
  const { setQueriesAction } = useReportsActions();

  const [state, setState] = useState<ReportFilters>(queriesFilters);
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
  const [filtersToPick, setFiltersToPick] = useState<FilterPickerState | null>(null);

  const filtersQuantity = Object.keys(state).length;

  const handleClearFilters = () => {
    setState({});
    // setSelectedFilters([]);
    setQueriesAction({ filters: {} });
    updateQueries({ filters: {} });
    setIsFiltersExpanded(false);
  };

  const removeFilter = (filter: string): void => {
    setState((prevState) => {
      const stateCopy = { ...prevState };
      delete stateCopy[filter];
      return stateCopy;
    });
    setIsFiltersExpanded(true);
  };

  const updateSpecificFilter = (e: React.MouseEvent, filter: string) => {
    setFiltersToPick({
      anchorEl: e.currentTarget,
      options: allFiltersOptions[filter].map(String),
      incomingValue: state[filter].map(String),
      onSubmit: (newValue) => setState((prevState) => ({ ...prevState, [filter]: newValue }))
    });
    setIsFiltersExpanded(true);
  };

  const updateAllFilters = (e: React.MouseEvent) => {
    setFiltersToPick({
      anchorEl: e.currentTarget,
      options: Object.keys(allFiltersOptions),
      incomingValue: Object.keys(state),
      onSubmit: (newValue) =>
        setState((prevState) => {
          const result: ReportFilters = newValue.reduce((acc, filter) => ({ ...acc, [filter]: [] }), {});

          // Keep previously selected values
          Object.entries(prevState).forEach(([filter, values]: [string, (string | number | boolean)[]]) => {
            if (!result[filter]) return;
            result[filter] = values;
          });

          return result;
        })
    });
    setIsFiltersExpanded(true);
  };

  const handleApplyFilters = () => {
    setQueriesAction({ filters: state });
    updateQueries({ filters: state });
    setIsFiltersExpanded(false);
  };

  const handleCloseFilterPicker = () => setFiltersToPick(null);

  useDidUpdate(() => {
    setState(queriesFilters);
  }, [queriesFilters]);

  return (
    <Stack sx={{ gap: "0px", width: "calc(100% - 120px)" }}>
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          flexWrap: "wrap",
          gap: "15px",
          maxHeight: "125px",
          overflowX: "hidden",
          overflowY: "auto"
        }}
      >
        <Button
          variant="outlined"
          color="secondary"
          size="sm"
          sx={{ borderColor: "#dae0e2" }}
          startIcon={<FilterAltOutlinedIcon sx={{ fontSize: "1.2rem", marginTop: "-3px" }} />}
          onClick={updateAllFilters}
        >
          Filters ({filtersQuantity})
        </Button>
        {Object.entries(state).map(([filter, values], index) => {
          if (isFiltersExpanded || filtersQuantity <= 2) {
            return (
              <Chip
                size="small"
                key={filter}
                label={`${toCapitalizeList?.includes(filter) ? capitalize(filter).replace(/_/g, " ") : filter} (${
                  values?.length
                })`}
                onDelete={() => removeFilter(filter)}
                onClick={(e) => updateSpecificFilter(e, filter)}
              />
            );
          } else {
            return (
              <>
                {index < 2 && (
                  <Chip
                    size="small"
                    key={filter}
                    label={`${toCapitalizeList?.includes(filter) ? capitalize(filter).replace(/_/g, " ") : filter} (${
                      values?.length
                    })`}
                    onDelete={() => removeFilter(filter)}
                    onClick={(e) => updateSpecificFilter(e, filter)}
                  />
                )}
                {index === filtersQuantity - 1 && (
                  <Chip
                    size="small"
                    key={filter}
                    label={`+${filtersQuantity - 2}`}
                    onClick={() => setIsFiltersExpanded((prevState) => !prevState)}
                  />
                )}
              </>
            );
          }
        })}
        {Boolean(filtersQuantity) && (
          <Button size="sm" sx={{ color: "#26A6AE" }} variant="text" onClick={handleClearFilters}>
            Clear All
          </Button>
        )}
      </Stack>
      <Collapse in={isFiltersExpanded}>
        <Stack direction="row" sx={{ p: "15px", alignItems: "center", gap: "10px" }}>
          <Button size="sm" variant="contained" onClick={handleApplyFilters} color="primary">
            Apply
          </Button>
          <Button
            size="sm"
            sx={{ color: "#26A6AE" }}
            variant="text"
            onClick={() => setIsFiltersExpanded((prevState) => !prevState)}
          >
            Close
          </Button>
        </Stack>
      </Collapse>
      <Popover
        open={Boolean(filtersToPick)}
        anchorEl={filtersToPick?.anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
        onClose={handleCloseFilterPicker}
        disableEnforceFocus
        disableAutoFocus
      >
        <FilterPicker {...filtersToPick} onCancel={handleCloseFilterPicker} onClose={handleCloseFilterPicker} />
      </Popover>
    </Stack>
  );
};
